import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  IAiCreditsRepository,
  AiCreditAccount,
  AiCreditLedgerEntry,
  AiCreditLedgerKind,
  AiTopupRequest,
} from '@erp/data-access/interfaces';

/** Postgres NUMERIC comes back from PostgREST as a string — coerce safely. */
function num(v: unknown): number {
  if (typeof v === 'number') return v;
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function rpcError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'message' in err) {
    return new Error(String((err as { message: string }).message));
  }
  return new Error(String(err));
}

interface AccountRow {
  balance_credits?: unknown;
  granted_initial?: boolean;
  lifetime_topup_credits?: unknown;
  lifetime_usage_credits?: unknown;
  lifetime_provider_cost_nano_usd?: unknown;
}

function mapAccount(r: AccountRow | null): AiCreditAccount {
  return {
    balanceCredits: num(r?.balance_credits),
    grantedInitial: !!r?.granted_initial,
    lifetimeTopupCredits: num(r?.lifetime_topup_credits),
    lifetimeUsageCredits: num(r?.lifetime_usage_credits),
    lifetimeProviderCostNanoUsd: num(r?.lifetime_provider_cost_nano_usd),
  };
}

interface LedgerRow {
  id: string;
  delta_credits: unknown;
  kind: string;
  feature: string | null;
  model: string | null;
  units: number | null;
  note: string | null;
  created_at: string;
}

function mapLedger(r: LedgerRow): AiCreditLedgerEntry {
  return {
    id: r.id,
    deltaCredits: num(r.delta_credits),
    kind: r.kind as AiCreditLedgerKind,
    feature: r.feature,
    model: r.model,
    units: r.units,
    note: r.note,
    createdAt: r.created_at,
  };
}

interface RequestRow {
  id: string;
  pack_code: string | null;
  credits: unknown;
  price_minor: unknown;
  currency: string;
  amount_dt?: unknown;
  status: string;
  created_at: string;
  fulfilled_at: string | null;
  canceled_at?: string | null;
  cancellation_note?: string | null;
}

function mapRequest(r: RequestRow): AiTopupRequest {
  const legacyMinor = Math.round(num(r.amount_dt) * 1_000);
  return {
    id: r.id,
    packCode: r.pack_code,
    credits: num(r.credits) || legacyMinor,
    priceMinor: num(r.price_minor) || legacyMinor,
    currency: r.currency || 'TND',
    status: r.status as AiTopupRequest['status'],
    createdAt: r.created_at,
    fulfilledAt: r.fulfilled_at,
    canceledAt: r.canceled_at ?? null,
    cancellationNote: r.cancellation_note ?? null,
  };
}

function isMissingPackOnlyRpc(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const value = error as { code?: unknown; message?: unknown };
  return (
    value.code === 'PGRST202' &&
    /ai_credit_request_topup\(p_pack_code\)|parameter p_pack_code/i.test(
      String(value.message ?? ''),
    )
  );
}

/** Per-tenant AI credit wallet. Reads via RLS; the free grant + request are
 *  SECURITY DEFINER RPCs. Debits happen server-side in the ai-generate Edge
 *  Function, never here. */
export class SupabaseAiCreditsRepository implements IAiCreditsRepository {
  constructor(private supabase: SupabaseClient) {}

  async getBalance(): Promise<AiCreditAccount> {
    const { data, error } = await this.supabase.rpc('ai_credit_balance');
    if (error) throw rpcError(error);
    return mapAccount(data as AccountRow | null);
  }

  async listLedger(limit = 50): Promise<AiCreditLedgerEntry[]> {
    const { data, error } = await this.supabase
      .from('ai_credit_ledger')
      .select(
        'id, delta_credits, kind, feature, model, units, note, created_at',
      )
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw rpcError(error);
    return ((data ?? []) as LedgerRow[]).map(mapLedger);
  }

  async listTopupRequests(limit = 20): Promise<AiTopupRequest[]> {
    const { data, error } = await this.supabase
      .from('ai_topup_requests')
      // Legacy-safe projection: the currently deployed backend does not yet
      // have the integer/cancellation columns.
      .select('id, pack_code, amount_dt, status, created_at, fulfilled_at')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw rpcError(error);
    return ((data ?? []) as RequestRow[]).map(mapRequest);
  }

  async requestTopup(
    packCode: string,
    priceMinor: number,
  ): Promise<AiTopupRequest> {
    if (!Number.isSafeInteger(priceMinor) || priceMinor <= 0) {
      throw new Error('invalid_credit_pack');
    }

    const current = await this.supabase.rpc('ai_credit_request_topup', {
      p_pack_code: packCode,
    });
    if (!current.error) return mapRequest(current.data as RequestRow);
    if (!isMissingPackOnlyRpc(current.error)) throw rpcError(current.error);

    // Rolling-deploy compatibility: production originally accepted the pack
    // plus its TND amount. The database still validates the pair.
    const legacy = await this.supabase.rpc('ai_credit_request_topup', {
      p_pack_code: packCode,
      p_amount_dt: priceMinor / 1_000,
    });
    if (legacy.error) throw rpcError(legacy.error);
    return mapRequest(legacy.data as RequestRow);
  }
}
